import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { loadAccount } from './financeAccountSlice';
import { loadCategory } from './categorySlice';

let accountData = {};
try {
  const storedAccount = localStorage.getItem('account');
  if (storedAccount) {
    accountData = JSON.parse(storedAccount);
  }
} catch (error) {
  console.error('Error parsing stored account data:', error);
  localStorage.removeItem('account'); // Clear corrupted data
}

const initialState = accountData || { isEmailSent: false, users: [] };

// Async thunks
export const requestLogin  = createAsyncThunk(
  'account/requestLogin',
  async ({ email, password }, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      const result = await api.login(email, password);
      localStorage.setItem('account', JSON.stringify({...result}));
      dispatch({ type: 'appMain/loadingEnd' });
      await api.loadUsers();
      
      dispatch(loadAccount());
      dispatch(loadCategory());

      return { loginResult: result};
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      // Convert error to serializable format
      const errorMessage = error.message || 'An error occurred';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const requestRegister = createAsyncThunk(
  'account/requestRegister',
  async ({ name, email, password, passwordConfirmation }, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' });
    try {
      const result = await api.register(name, email, password, passwordConfirmation);
      dispatch({ type: 'appMain/loadingEnd' });
      return result;
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      // Convert error to serializable format
      const errorMessage = error.message || 'An error occurred';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

export const requestLoginGoogle = createAsyncThunk(
  'account/requestLoginGoogle',
  async ({ user, token }, { dispatch, getState, extra: api }) => {
    dispatch({ type: 'appMain/loadingStart' }); 
    try {      
      const result = { user, token };
      localStorage.setItem('account', JSON.stringify({...result}));
      dispatch({ type: 'appMain/loadingEnd' });
      const usersResult = await api.loadUsers();

      dispatch(loadAccount());
      dispatch(loadCategory());

      return { user, token, users: usersResult.items };
    } catch (error) {
      dispatch({ type: 'appMain/loadingEnd' });
      // Convert error to serializable format
      const errorMessage = error.message || 'An error occurred';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

// Add a separate thunk for loading users
export const loadUsers = createAsyncThunk(
  'account/loadUsers',
  async (_, { dispatch, getState, extra: api }) => {
    try {
      const usersResult = await api.loadUsers();
      return usersResult.items;
    } catch (error) {
      // Convert error to serializable format
      const errorMessage = error.message || 'An error occurred';
      dispatch({ 
        type: 'appMain/showMessage', 
        payload: { 
          message: errorMessage,
          severity: 'error',
          type: 'error'
        } 
      });
      throw error;
    }
  }
);

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    loginSuccess: (state, action) => {
      const userData = {
        ...state,
        user: { ...action.payload.user },
        token: action.payload.token
      };
      localStorage.setItem('account', JSON.stringify(userData));
      return userData;
    },
    logoff: () => {
      localStorage.removeItem('account');
      return {};
    },
    logoffUser: () => {
      localStorage.removeItem('account');
      return {};
    },
    storeUsers: (state, action) => {
      const userData = {
        ...state,
        users: action.payload
      };
      localStorage.setItem('account', JSON.stringify(userData));
      return userData;
    },
    registerSuccess: (state) => {
      return {
        ...state,
        isEmailSent: true
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestLogin.fulfilled, (state, action) => {
        console.log('Login successful, saving to localStorage:', action.payload);
        const userData = {
          ...state,
          user: { ...action.payload.loginResult.user },
          token: action.payload.loginResult.token,
          users: action.payload.users || state.users || []
        };
        localStorage.setItem('account', JSON.stringify(userData));
        console.log('User data saved to localStorage:', userData);
        return userData;
      })
      .addCase(requestRegister.fulfilled, (state) => {
        return {
          ...state,
          isEmailSent: true
        };
      })
      .addCase(requestLoginGoogle.fulfilled, (state, action) => {
        const userData = {
          ...state,
          user: { ...action.payload.user },
          token: action.payload.token,
          users: action.payload?.users || state.users || []
        };
        localStorage.setItem('account', JSON.stringify(userData));
        return userData;
      })
      .addCase(loadUsers.fulfilled, (state, action) => {
        const userData = {
          ...state,
          users: action.payload
        };
        localStorage.setItem('account', JSON.stringify(userData));
        return userData;
      });
  }
});

export const { loginSuccess, logoff, logoffUser, storeUsers, registerSuccess } = accountSlice.actions;

export default accountSlice.reducer;
